import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  writeBatch,
  Firestore,
} from 'firebase/firestore';

export interface UserNotification {
  id: string;
  type: 'broadcast' | 'group' | 'meeting' | 'system';
  title: string;
  message: string;
  createdAt: string;
  read: boolean;
  userId: string;
  // Optional context data
  groupId?: string;
  meetingId?: string;
  broadcastId?: string;
  groupName?: string;
  meetingTitle?: string;
  // Additional metadata
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  category?: string;
  actionUrl?: string; // URL to navigate to for more details
}

export interface UseNotificationsReturn {
  notifications: UserNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  clearAll: () => Promise<void>;
  refresh: () => void;
}

export const useNotifications = (
  firestore: Firestore,
  userId?: string,
): UseNotificationsReturn => {
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
  }, []);

  // Mark single notification as read
  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!firestore || !userId) return;

      try {
        const notificationRef = doc(firestore, 'notifications', notificationId);
        await updateDoc(notificationRef, {
          read: true,
          readAt: new Date().toISOString(),
        });
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
        setError('Failed to mark notification as read');
      }
    },
    [firestore, userId],
  );

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!firestore || !userId) return;

    try {
      const batch = writeBatch(firestore);
      const unreadNotifications = notifications.filter(n => !n.read);

      unreadNotifications.forEach(notification => {
        const notificationRef = doc(firestore, 'notifications', notification.id);
        batch.update(notificationRef, {
          read: true,
          readAt: new Date().toISOString(),
        });
      });

      await batch.commit();
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      setError('Failed to mark all notifications as read');
    }
  }, [firestore, userId, notifications]);

  // Delete single notification
  const deleteNotification = useCallback(
    async (notificationId: string) => {
      if (!firestore || !userId) return;

      try {
        const notificationRef = doc(firestore, 'notifications', notificationId);
        await deleteDoc(notificationRef);
      } catch (err) {
        console.error('Failed to delete notification:', err);
        setError('Failed to delete notification');
      }
    },
    [firestore, userId],
  );

  // Clear all notifications
  const clearAll = useCallback(async () => {
    if (!firestore || !userId) return;

    try {
      const batch = writeBatch(firestore);

      notifications.forEach(notification => {
        const notificationRef = doc(firestore, 'notifications', notification.id);
        batch.delete(notificationRef);
      });

      await batch.commit();
    } catch (err) {
      console.error('Failed to clear all notifications:', err);
      setError('Failed to clear all notifications');
    }
  }, [firestore, userId, notifications]);

  // Set up real-time listener
  useEffect(() => {
    if (!firestore || !userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const notificationsRef = collection(firestore, 'notifications');
      // Start with just the where clause to avoid index issues
      const notificationsQuery = query(notificationsRef, where('userId', '==', userId));

      const unsubscribe = onSnapshot(
        notificationsQuery,
        querySnapshot => {
          const notificationsList: UserNotification[] = [];
          querySnapshot.forEach(doc => {
            const data = doc.data();
            notificationsList.push({
              id: doc.id,
              ...data,
            } as UserNotification);
          });

          // Sort manually by createdAt descending
          notificationsList.sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );

          setNotifications(notificationsList);
          setLoading(false);
          console.log(
            `Loaded ${notificationsList.length} notifications for user ${userId}`,
          );
        },
        err => {
          console.error('Error fetching notifications:', err);
          console.error('Error details:', err.code, err.message);
          setError(`Failed to load notifications: ${err.message}`);
          setLoading(false);
        },
      );

      return unsubscribe;
    } catch (err) {
      console.error('Error setting up notifications listener:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to setup notifications listener: ${errorMessage}`);
      setLoading(false);
      return undefined;
    }
  }, [firestore, userId]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    refresh,
  };
};

// Helper function to create notifications (used by broadcast system)
export const createNotification = async (
  firestore: Firestore,
  notification: Omit<UserNotification, 'id' | 'createdAt'>,
): Promise<void> => {
  try {
    const notificationsRef = collection(firestore, 'notifications');
    await addDoc(notificationsRef, {
      ...notification,
      createdAt: new Date().toISOString(),
      read: false,
    });
  } catch (error) {
    console.error('Failed to create notification:', error);
    throw error;
  }
};
